import { proxy, useSnapshot } from 'valtio';

import { QUERY_PARAM_NATIVE_EMBED_FULLSCREEN } from '@canvas/constants/query-params';

import { getChildClient } from './bridge';

interface NativeEmbedState {
  isFullscreen: boolean;
  isSelected: boolean;
  showTitle: boolean;
}

export const nativeEmbedState = proxy<NativeEmbedState>({
  isFullscreen: false,
  isSelected: false,
  showTitle: true,
});

export function initStateFromUrl(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  nativeEmbedState.isFullscreen = params.has(QUERY_PARAM_NATIVE_EMBED_FULLSCREEN);
}

export function resetStateForTesting(): void {
  nativeEmbedState.isFullscreen = false;
  nativeEmbedState.isSelected = false;
  nativeEmbedState.showTitle = true;
  commandListenersInitialized = false;
}

let commandListenersInitialized = false;

export function initCommandListeners(): void {
  if (commandListenersInitialized || !getChildClient()) {
    return;
  }
  commandListenersInitialized = true;
  handleSelectedChange();
  handleAlwaysShowTitleChange();
}

function handleSelectedChange(): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.onSelectedChange(({ selected }) => {
    nativeEmbedState.isSelected = selected;
  });
}

function handleAlwaysShowTitleChange(): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.onAlwaysShowTitleChange(({ alwaysShowTitle }) => {
    nativeEmbedState.showTitle = alwaysShowTitle;
  });
}

export function toggleFullscreen(): void {
  const client = getChildClient();
  if (!client) {
    return;
  }

  client.sendToggleFullScreen();
}

interface UseFullscreenResult {
  isFullscreen: boolean;
  toggleFullscreen: () => void;
}

export function useFullscreen(): UseFullscreenResult {
  const { isFullscreen } = useSnapshot(nativeEmbedState);
  return {
    isFullscreen,
    toggleFullscreen,
  };
}

export function useShowTitle(): boolean {
  const { showTitle } = useSnapshot(nativeEmbedState);
  return showTitle;
}
